<template>
	<section v-if="badges" ref="el" data-component="club-details" data-section-intersect="light" :data-anchor="$t('resume', 'Resumo')">
		<div class="row align-center title-row">
			<div class="xxlarge-16 medium-14 small-16 col">
				<h2 class="-title-4 -uppercase title">{{ title }}</h2>
				<p class="-headline -uppercase subtitle">{{ subtitle }}</p>
			</div>
		</div>

		<div v-if="badgesRef" :class="`animation-container ${badgesRef.file ? 'is-rive' : ''}`" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
			<ClientOnly>
				<component :is="RiveAsset" v-if="RiveAsset" ref="riveAnimation" :asset="badgesRef" state-machine="State Machine 1" :autoplay="true" layout="contain" hydrate-on-idle />
			</ClientOnly>
		</div>

		<div v-if="details" class="details-list">
			<div v-for="(item, i) in details" :key="i" class="list-col">
				<h3 class="-body-1-medium -uppercase item-title">{{ item.title }}</h3>
				<div class="content">
					<RichText :blocks="item.content" />
				</div>
			</div>
		</div>
	</section>
</template>

<script setup>
import { gsap, ScrollTrigger } from 'gsap/all'
import.meta.client && gsap.registerPlugin(ScrollTrigger)

// import { RiveAsset } from '~/components/core'
import { RichText } from '~/components/primitives'

import { useStringTranslation, useLocale } from '@/i18n'

import { useScreen } from 'vue-screen'
import { breakpoints } from '~/config'

const props = defineProps({
	title: { type: String, default: '' },
	subtitle: { type: String, default: '' },
	badges: { type: [Boolean, Object], default: false },
	details: { type: [Boolean, Array], default: false },
})

const context = inject('pageContext')
const el = ref(null)
const riveAnimation = ref(null)
const $t = useStringTranslation()
const locale = useLocale()
const screen = useScreen()

const badgesRef = ref(false)

const RiveAsset = shallowRef(null)

let observer

const handleMouseEnter = () => {
	riveAnimation.value?.loaded().then(() => {
		riveAnimation.value.getInput('hover').value = true
	})
}

const handleMouseLeave = () => {
	riveAnimation.value?.loaded().then(() => {
		riveAnimation.value.getInput('hover').value = false
	})
}

const setup = () => {}
const init = () => {
	riveAnimation.value?.loaded().then(() => {
		if (riveAnimation.value.getInput('language')) {
			riveAnimation.value.getInput('language').value = locale.value === 'pt' ? 0 : 1
		}

		observer = ScrollTrigger.create({
			trigger: el.value.querySelector('.animation-container'),
			start: 'top bottom',
			onEnter: () => {
				if (riveAnimation.value?.getInput('start')) riveAnimation.value.getInput('start').value = true
				observer.kill()
			},
			onEnterBack: () => {
				if (riveAnimation.value?.getInput('start')) riveAnimation.value.getInput('start').value = true
				observer.kill()
			},
		})
	})
}
const kill = () => {
	observer?.kill()
}

onBeforeUnmount(() => kill())
onMounted(async () => {
	const module = await import('~/components/core/RiveAsset/RiveAsset.vue')
	RiveAsset.value = module.default

	badgesRef.value = screen.width > breakpoints.xsmall ? props.badges : { file: props.badges.mobileFile, fallbackImage: props.badges.fallbackImage }

	setTimeout(() => {
		context.$page.loader.loaded.then(() => setup())
		context.$page.loader.ready.then(() => init())
	}, 1000)
})
</script>

<style lang="scss" scoped>
[data-component='club-details'] {
	position: relative;

	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: flex-start;
	padding: rs(60) 0;
	gap: rs(80);

	background-color: var(--color-darkBrown);
	color: var(--color-subduedYellow);

	width: 100%;
	overflow: hidden;

	.title-row {
		.col {
			text-align: center;

			display: flex;
			flex-direction: column;
			gap: rs(20);

			.subtitle {
				padding: 0 n_grid_columns(2.5);

				@include max-screen($xsmall) {
					padding: 0;
				}
			}
		}
	}

	.animation-container {
		width: n_grid_columns(8);

		&.is-rive {
			width: n_grid_columns(16);

			@include max-screen($xsmall) {
				margin-top: -5vh;
				width: n_grid_columns(32);
				height: 40vh;

				pointer-events: none;
			}
		}

		@include max-screen($small) {
			width: n_grid_columns(12);
		}
		@include max-screen($xsmall) {
			width: n_grid_columns(16);
		}
	}

	.details-list {
		display: flex;
		gap: rs(48);
		padding: 0 calc(var(--grid-padding) + var(--grid-gutter));

		@include max-screen($xsmall) {
			gap: n_grid_columns(1);
		}
		.list-col {
			display: flex;
			flex-direction: column;
			gap: rs(4);
			flex: 1;
			padding: 0 n_grid_columns(0.5);

			// @include max-screen($small) {
			// 	flex: 0 0 n_grid_columns(4);
			// 	width: n_grid_columns(4);
			// }
		}

		.content {
			color: var(--color-lightYellow);

			:deep(p) {
				white-space: pre-line;
				margin-bottom: 1em;
				@include body-1;
			}

			:deep(a) {
				text-decoration: underline;
			}

			:deep(h3) {
				position: relative;
				margin-top: -1em;
				@include footnote-1;
			}
		}
	}
}
</style>
